import { useFormik } from "formik";
import React, { useEffect, useState, useTransition } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import * as Yup from "yup";
import checkIcon from "../../../assets/icons/check-icon-blue.svg";
import eyeIcon from "../../../assets/icons/eye-icon-white.svg";
import eyeSlashedIcon from "../../../assets/icons/eye-slashed-icon-white.svg";
import googleLogo from "../../../assets/icons/google-icon-colored.svg";
import logoIcon from "../../../assets/icons/acg-logo.png";
import customStyles from "../../../customStyles";
import { country } from "../../../utils/constants/country";
import { validateEmail } from "../../../utils/helpers/string";
import { returnErrors } from "../../../utils/store/reducers/Error";
import { signup } from "../../../utils/store/reducers/authSlice";
import "./style.scss";
import Spinner from "../../../reusableComponents/spinner/Spinner";
import { pushLeads } from "../../../utils/apis/payment";
import { Helmet } from "react-helmet";
import ReCAPTCHA from "react-google-recaptcha";
import { Calendar, DatePicker } from "antd/lib";
import moment from "moment";
import dayjs from "dayjs";
import { deAuthenticateAll } from "../../../utils/store/reducers/Index";
import { returnMessages } from "../../../utils/store/reducers/Message";

const SignupForm = ({ mode }) => {
  const countryOptions = country.flat();

  const { userId } = useSelector((state) => state.auth);

  const inputs = {
    first_name: "",
    last_name: "",
    country: "United Kingdom",
    contact: "",
    email: "",
    password: "",
    country_code: "44",
    dob: "",
    referal_code: mode === "ref" ? window.location.pathname.split("/")[2] : "",
  };

  const nameRegex = /^[A-Za-z\s'-]+$/;

  const schema = Yup.object().shape({
    first_name: Yup.string()
      .required("First Name is required")
      .min(2, "First Name must be at least 2 characters long")
      .matches(
        nameRegex,
        "First Name can only contain letters, spaces, hyphens, or apostrophes"
      ),

    last_name: Yup.string()
      .required("Last Name is required")
      .min(2, "Last Name must be at least 2 characters long")
      .matches(
        nameRegex,
        "Last Name can only contain letters, spaces, hyphens, or apostrophes"
      ),
    country: Yup.string()
      .required("Country is required")
      .lowercase()
      .oneOf(
        countryOptions.map((item) => item.country?.toLowerCase()),
        "Invalid country"
      ),
    contact: Yup.string()
      .required("Number is required")
      .max(15, "Contact number can't be longer than 15 characters"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    password: Yup.string()
      .required("Password is required")
      .min(8, "Password must be at least 8 characters long")
      .max(16, "Password must not exceed 16 characters"),

    country_code: Yup.string()
      .transform((value) => value.replace(/^\+/, "")) // strip leading +
      .oneOf(
        country.map((item) => item.code),
        "Invalid country code"
      )
      .required("Country code is required"),
    dob: Yup.string()
      .required("Date of Birth is required")
      .test("is-valid-date", "Invalid date", (value) =>
        value ? dayjs(value, "YYYY-MM-DD", true).isValid() : false
      ),
  });
  const referalcode =
    mode === "ref" ? window.location.pathname.split("/")[2] : "";

  useEffect(() => {
    const sendReferralCode = async () => {
      if (referalcode) {
        try {
          let formDataPL = new FormData();
          formDataPL.append("referal_code", referalcode);
          await pushLeads(formDataPL);
        } catch (error) {
          console.error("Error pushing leads:", error);
        }
      }
    };

    sendReferralCode();
  }, [referalcode, mode]);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [recaptchaToken, setRecaptchaToken] = useState(null);

  const onChange = (token) => {
    setRecaptchaToken(token);
  };
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    isSubmitting,
  } = useFormik({
    initialValues: inputs,
    validationSchema: schema,
    onSubmit: async (values, { setSubmitting }) => {
      if (recaptchaToken) {
        try {
          const res = await dispatch(signup(values));
          if (res.type === "auth/signup/fulfilled") {
            dispatch(
              returnMessages(res?.payload?.detail ||"Signup successful. Please log in.", 200)
            );
            deAuthenticateAll(dispatch);
          }
          if (res.payload.status >= 400) {
            const error = res.payload.data;
            let msg;
            if (Object.keys(error).length > 0) {
              const firstKey = Object.keys(error)[0];
              const firstString = error[firstKey] || error[firstKey][0];
              msg = firstString;
            } else {
              msg = "Something went wrong";
            }
            dispatch(returnErrors(msg, res.payload.status));
          } else {
            // Navigate or show success message
            // navigate("/");
          }
        } catch (error) {
          console.error("Error occurred during signup:", error);
        } finally {
          setSubmitting(false);
        }
      } else {
        dispatch(returnErrors("Please complete the reCAPTCHA", 400));
        setSubmitting(false); // Ensure the form does not stay in submitting state
      }
    },
  });

  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  useState(false);
  const [isGoogleAuthLoading, setIsGoogleAuthLoading] = useState(false);
  // const liveUrl = "https://app.alpha-futures.com";
  const liveUrl = "https://app.alpha-futures.com";
  // const liveUrl = "http://localhost:3000";
  const handleGoogleAuth = async (e) => {
    e.preventDefault();
    setIsGoogleAuthLoading(true);

    try {
      const path = `https://accounts.google.com/o/oauth2/v2/auth?scope=https://www.googleapis.com/auth/userinfo.email&include_granted_scopes=true&response_type=code&state=pass-through value&redirect_uri=${liveUrl}/auth/google&client_id=889129287888-8dcqpkvg0e2mtk59vjr4mi5dedrj08ep.apps.googleusercontent.com`;

      // Create a promise that resolves after the redirection
      const googleAuthPromise = new Promise((resolve) => {
        window.location.replace(path);
        resolve();
      });

      // Await the completion of the promise
      await googleAuthPromise;
    } finally {
      // Set loading state to false when Google Auth finishes
      // setIsGoogleAuthLoading(false);
    }
  };

  return (
    <div className="signupform-container">
      <Helmet>
        <script>
          {`
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              'event' : 'registration',
              'authentication_method' : 'email',
              'user_id' : '${userId}'
            });
          `}
        </script>
      </Helmet>
      <div className="signupform-wrapper">
        <div className="company-logo">
          <img src={logoIcon} alt="company-logo" />
        </div>
        <div className="signup-form-info">
          <h2 className="form-header">Sign Up</h2>
        </div>
        <form className="signupform-innnercontainer">
          <div className="signup-options" onClick={handleGoogleAuth}>
            {isGoogleAuthLoading ? (
              <Spinner />
            ) : (
              <>
                <img src={googleLogo} alt="Google logo" />
                <p>Continue with Google</p>
              </>
            )}
          </div>
          <div className="form-separator">
            <div className="left"></div>
            <p>Or log in using</p>
            <div className="left"></div>
          </div>
          <div
            className="signupform-subcontainer-form"
            style={{
              gap:
                (touched.email && errors.email) ||
                (touched.first_name && errors.first_name) ||
                (touched.last_name && errors.last_name) ||
                (touched.country && errors.country) ||
                (touched.country_code && errors.country_code) ||
                (touched.contact && errors.contact) ||
                (touched.password && errors.password) ||
                (touched.confirmPassword && errors.confirmPassword)
                  ? "20px"
                  : "30px",
            }}
          >
            <div className="input-wrapper">
              <p className="input-label">Email</p>
              <label
                className={`${`signupform-container-input `}${
                  touched.email && errors.email && "form-empty-input"
                }`}
                htmlFor="email"
              >
                <input
                  type="text"
                  className="signupform-input-box"
                  name="email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Your Email address"
                  id="email"
                />
                {validateEmail(values.email) && <img src={checkIcon} alt="" />}
              </label>
            </div>
            {touched.email && errors.email && (
              <div className="form-error-message">{errors.email}</div>
            )}
            <div className="input-wrapper">
              <p className="input-label">First Name</p>
              <label
                className={`${`signupform-container-input `}${
                  touched.first_name && errors.first_name && "form-empty-input"
                }`}
                htmlFor="first_name"
              >
                <input
                  type="text"
                  className="signupform-input-box"
                  name="first_name"
                  value={values.first_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="First name"
                  id="first_name"
                />
              </label>
            </div>
            {touched.first_name && errors.first_name && (
              <div className="form-error-message">{errors.first_name}</div>
            )}
            <div className="input-wrapper">
              <p className="input-label">Last Name</p>
              <label
                className={`${`signupform-container-input `}${
                  touched.last_name && errors.last_name && "form-empty-input"
                }`}
                htmlFor="last_name"
              >
                <input
                  type="text"
                  className="signupform-input-box"
                  name="last_name"
                  value={values.last_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Last name"
                  id="last_name"
                />
              </label>
            </div>
            {touched.last_name && errors.last_name && (
              <div className="form-error-message">{errors.last_name}</div>
            )}
            <div className="input-wrapper">
              <p className="input-label">Country</p>
              <label
                className={`${`signupform-container-input signupform-country-selector `}${
                  touched.country && errors.country && "form-empty-input"
                }`}
                htmlFor="country"
              >
                <Select
                  className="signupform-input-box react-select-container"
                  classNamePrefix="react-select"
                  name="country"
                  value={{ label: values.country, value: values.country }}
                  styles={customStyles}
                  onChange={(selectedOption) => {
                    setFieldValue("country", selectedOption.label);
                    setFieldValue("country_code", selectedOption.value);
                  }}
                  onBlur={handleBlur}
                  placeholder="Select country"
                  id="country"
                  options={countryOptions.map((item) => ({
                    value: item.code,
                    label: item.country,
                  }))}
                  isSearchable={true}
                />
              </label>
            </div>
            {touched.country && errors.country && (
              <div className="form-error-message">{errors.country}</div>
            )}
            <div className="input-wrapper">
              <p className="input-label">Date of Birth</p>
              <label
                className={`${`signupform-container-input signupform-dob-selector `}${
                  touched.dob && errors.dob && "form-empty-input"
                }`}
                htmlFor="dob"
                style={{ padding: "0px 20px", overflow: "hidden" }}
              >
                <DatePicker
                  onChange={(date) => {
                    setFieldValue(
                      "dob",
                      date ? dayjs(date).format("YYYY-MM-DD") : ""
                    );
                  }}
                  onBlur={handleBlur}
                  value={values.dob ? dayjs(values.dob) : null}
                  inputReadOnly
                  disabledDate={(current) =>
                    current && current.isAfter(dayjs(), "day")
                  }
                  placeholder="Date of Birth"
                />
              </label>
            </div>
            {touched.dob && errors.dob && (
              <div className="form-error-message">{errors.dob}</div>
            )}

            <div className="input-wrapper">
              <p className="input-label">Contact</p>
              <label
                className="signupform-container-input signupform-contact-container"
                htmlFor="contact"
              >
                <label
                  className={`${`country-code-box `}${
                    touched.country_code &&
                    errors.country_code &&
                    "form-empty-input"
                  }`}
                  htmlFor="country_code"
                >
                  <span className="country-code-input">+</span>
                  <input
                    type="text"
                    value={values.country_code}
                    onChange={(e) => {
                      const onlyNumbersAndDash = e.target.value.replace(
                        /[^0-9-]/g,
                        ""
                      );
                      setFieldValue("country_code", onlyNumbersAndDash);
                    }}
                    onBlur={handleBlur}
                    name="country_code"
                    placeholder="Country Code"
                    id="country_code"
                  />
                </label>

                <label
                  className={`${`contact-number-box `}${
                    touched.contact && errors.contact && "form-empty-input"
                  }`}
                  htmlFor="contact"
                >
                  <input
                    type="number"
                    className="signupform-input-box"
                    name="contact"
                    value={values.contact}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Phone Number"
                    id="contact"
                  />
                </label>
              </label>
            </div>
            {touched.country_code && errors.country_code && (
              <div className="form-error-message">{errors.country_code}</div>
            )}
            {touched.contact && errors.contact && (
              <div className="form-error-message">{errors.contact}</div>
            )}
            <div className="input-wrapper">
              <p className="input-label">Password</p>
              <label
                className={`${`signupform-container-input `}${
                  touched.password && errors.password && "form-empty-input"
                }`}
                htmlFor="password"
              >
                <input
                  type={isPasswordVisible ? "text" : "password"}
                  className="signupform-input-box"
                  name="password"
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter Password"
                  id="password"
                />
                <img
                  style={{ cursor: "pointer" }}
                  src={isPasswordVisible ? eyeIcon : eyeSlashedIcon}
                  alt=""
                  onClick={() => setIsPasswordVisible((prev) => !prev)}
                />
              </label>
            </div>
            {touched.password && errors.password && (
              <div className="form-error-message">{errors.password}</div>
            )}
            <ReCAPTCHA
              sitekey="6LeJEjEqAAAAAIxK6hUEYE4aWskY7L-jTZ7e14NF"
              onChange={onChange}
            />
          </div>
          <div className="signupform-submit-options">
            <button
              className="submit-button standard-button"
              onClick={handleSubmit}
              type="submit"
            >
              {isSubmitting ? <Spinner /> : "Submit"}
            </button>
            <p>
              Already have an account?{" "}
              <span>
                <a href="/signin">Sign In</a>
              </span>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SignupForm;
